import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, CalendarDays, MapPin } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { EASE } from "../components/motion";
import { useLang } from "../lib/i18n";

export default function ClientPresence() {
  const { token } = useParams();
  const { t } = useLang();
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/appointments/${token}`)
      .then((r) => setAppt(r.data))
      .catch((e) => setError(formatApiError(e, t("presence.notFound"))));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${token}/presence`);
      setAppt(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dateLabel = appt ? (() => {
    const d = new Date(`${appt.date}T12:00:00`);
    const jsDay = (d.getDay() + 6) % 7;
    return `${t("days")[jsDay]} ${d.getDate()} ${t("months")[d.getMonth()]}`;
  })() : "";

  return (
    <div className="min-h-screen bg-cream grain-overlay flex items-center justify-center px-6 py-16" data-testid="presence-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-lg bg-white border border-taupe/20 rounded-3xl p-10 shadow-[0_8px_30px_rgba(44,36,27,0.06)] text-center"
      >
        <img src="/logo.png" alt="Nadou Skin" className="h-12 w-auto mx-auto" />
        {error ? (
          <p className="mt-8 text-sm text-[#9E4747]" data-testid="presence-error">{error}</p>
        ) : !appt ? (
          <div className="mt-8 flex items-center justify-center gap-3 text-sm text-espresso/55">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("presence.loading")}
          </div>
        ) : appt.presence_confirmed ? (
          <div className="mt-8" data-testid="presence-done">
            <div className="w-16 h-16 rounded-full bg-[#4A6B53]/10 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-[#4A6B53]" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-3xl text-espresso mt-6">{t("presence.doneTitle", { name: appt.name.split(" ")[0] })}</h1>
            <p className="mt-3 text-sm text-espresso/65 font-light leading-relaxed">
              {t("presence.doneText")}{" "}
              <span className="font-medium text-espresso">{appt.service_name}</span>, {dateLabel} · {appt.time}.
            </p>
            <p className="mt-6 text-xs text-espresso/45 flex items-center justify-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> Chaussée de Waterloo 80, 1060 Saint-Gilles
            </p>
          </div>
        ) : (
          <div className="mt-8" data-testid="presence-details">
            <div className="flex items-center justify-center gap-3 text-taupe-dark">
              <CalendarDays className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[12px] tracking-[0.2em] uppercase">{t("presence.kicker")}</span>
            </div>
            <h1 className="font-display text-3xl text-espresso mt-4">{appt.service_name}</h1>
            <p className="mt-3 text-sm text-espresso/65 font-light">
              {dateLabel} · {appt.time} — {appt.name}
            </p>
            <p className="mt-2 text-xs text-espresso/45 flex items-center justify-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> Chaussée de Waterloo 80, 1060 Saint-Gilles
            </p>
            <button
              data-testid="presence-confirm-btn"
              disabled={busy}
              onClick={confirm}
              className="mt-8 w-full rounded-full bg-taupe text-cream px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-taupe-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-300 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={1.5} />}
              {t("presence.btn")}
            </button>
          </div>
        )}
        <Link to="/" className="block mt-8 text-xs tracking-[0.14em] uppercase text-espresso/45 hover:text-espresso transition-colors duration-300">
          {t("presence.back")}
        </Link>
      </motion.div>
    </div>
  );
}
